import React, { useState, useEffect, useRef } from 'react';
import { Text } from '@visx/text';
import { scaleLog } from '@visx/scale';
import Wordcloud from '@visx/wordcloud/lib/Wordcloud';

const colors = ['#143059', '#2F6B9A', '#82a6c2'];

const WordCloud = ({ onWordClick }) => {
  const containerRef = useRef(null);
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dimensions, setDimensions] = useState({
    width: 600,
    height: 400
  });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height: rect.height - 60 // Subtract header height
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiUrl = process.env.NODE_ENV === 'development'
          ? 'http://localhost:3001/api/profession-stats'
          : '/api/profession-stats';

        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        
        const data = await response.json();
        
        const formattedWords = Object.entries(data).map(([text, value]) => ({
          text,
          value: typeof value === 'number' ? value : 1
        }));

        setWords(formattedWords);
        setError(null);
      } catch (err) {
        setError(err.message);
        setWords([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fontScale = scaleLog({
    domain: words.length > 0 
      ? [Math.min(...words.map(w => w.value)), Math.max(...words.map(w => w.value))]
      : [1, 10],
    range: [15, 40],
  });

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div 
        ref={containerRef} 
        className="bg-white rounded-lg shadow-md relative h-[400px]"
      >
        <h2 className="text-xl font-semibold p-4 text-center">
          Most Analyzed Professions
        </h2>
        
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-gray-500">Loading...</div>
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-red-500">Error: {error}</div>
          </div>
        ) : words.length > 0 ? (
          <div className="absolute inset-0 pt-16">
            <svg 
              width={dimensions.width} 
              height={dimensions.height}
              className="bg-transparent"
            >
              <Wordcloud
                words={words}
                width={dimensions.width}
                height={dimensions.height}
                fontSize={w => fontScale(w.value)}
                font="Impact"
                padding={5}
                spiral="archimedean"
                rotate={0}
                random={() => 0.5}
              >
                {(cloudWords) => (
                  <g transform={`translate(0, 0)`}>
                    {cloudWords.map((w, i) => (
                      <Text
                        key={w.text}
                        fill={colors[i % colors.length]}
                        textAnchor="middle"
                        transform={`translate(${w.x}, ${w.y})`}
                        fontSize={w.size}
                        fontFamily={w.font}
                        style={{ cursor: onWordClick ? 'pointer' : 'default' }}
                        onMouseEnter={e => { if (onWordClick) e.target.style.opacity = '0.65'; }}
                        onMouseLeave={e => { if (onWordClick) e.target.style.opacity = '1'; }}
                        onClick={() => onWordClick && onWordClick(w.text)}
                      >
                        {w.text}
                      </Text>
                    ))}
                  </g>
                )}
              </Wordcloud>
            </svg>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-gray-500">No profession data available</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WordCloud;